(ns practitest.frontend.find-and-replace-modal.main
  (:require
    [clojure.string :as str]
    [practitest.frontend.common-entities.metadata :refer [get-entity-name]]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.components.grid.subs :as grid.subs]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.selector :as selector]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.components-provider :refer [application-provider]]
    [practitest.frontend.components2.info-note :as info-note]
    [practitest.frontend.find-and-replace-modal.events :as events]
    [practitest.frontend.find-and-replace-modal.subs :as subs]
    [re-frame.core :as re-frame]))

;; Shape mirrors practitest.frontend.batch-modal (header/body/footer via components.modal).

(defn- selection-count [entity selection state-key]
  (if (= selection :_all)
    @(re-frame/subscribe [::grid.subs/entities-total-count entity state-key])
    (count selection)))

(defn- selection-ids-info [entity selection state-key]
  (when (set? selection)
    (let [rows @(re-frame/subscribe [::grid.subs/data [entity state-key]])
          id->display-id (into {} (map (juxt :id :display_id)) rows)
          display-ids (keep id->display-id selection)
          truncated? (> (count selection) 5)]
      (when (seq display-ids)
        {:text (str (str/join ", " (take 5 display-ids))
                    (when truncated? "..."))
         :full-text (str/join ", " display-ids)
         :truncated? truncated?}))))

(defn- selection-ids-span [ids-info]
  (if (:truncated? ids-info)
    [tooltip/mux-tooltip
     {}
     (:full-text ids-info)
     (fn [props] [:span.selection-ids props (:text ids-info)])]
    [:span.selection-ids (:text ids-info)]))

(defn- selection-summary [entity selection state-key]
  (let [entity-label (get-entity-name entity :plural-name)
        count (selection-count entity selection state-key)
        ids-info (selection-ids-info entity selection state-key)]
    [:div.selection-summary
     (if ids-info
       (let [[before after] (str/split
                               (format-translation
                                 (get-translation "find-and-replace-modal.selection-summary"
                                                  "%{count} %{entity} Selected · %{ids}")
                                 {:count count :entity entity-label})
                               #"%\{ids\}" 2)]
         [:<>
          before
          [selection-ids-span ids-info]
          after])
       (format-translation
         (get-translation "find-and-replace-modal.selection-summary-no-ids"
                          "%{count} %{entity} Selected")
         {:count count :entity entity-label}))]))

(defn- header [entity selection state-key]
  (let [entity-label (get-entity-name entity :plural-name)]
    [:div.title-area
     [:div.header-row
      [:span.header-icon [icons/find-and-replace]]
      [:div
       [:div.title
        (get-translation "entity.button.find_and_replace" "Find & Replace")
        [:span.entity " | " entity-label]]
       [:span.subtitle
        (get-translation "find-and-replace-modal.subtitle"
                         "Find and Replace – Replace text across selected fields")]]]
     [selection-summary entity selection state-key]]))

;; .m-label + .mandatory reuse batch-modal.scss's positioning (already active via the "batch-modal" class).
(defn- field-label [text mandatory?]
  [:label.m-label text (when mandatory? [:span.mandatory "*"])])

(defn- body []
  (let [fields @(re-frame/subscribe [::subs/fields])
        find-val @(re-frame/subscribe [::subs/find-val])
        replace-val @(re-frame/subscribe [::subs/replace-val])
        selected-fields @(re-frame/subscribe [::subs/selected-fields])
        message @(re-frame/subscribe [::subs/message])
        sorted-fields (sort-by second fields)]
    [:div.find-and-replace-body
     [:div.g2-field-container
      [field-label (get-translation "find-and-replace-modal.find.label" "Find") true]
      [:input.m-input
       {:type "text"
        :value find-val
        :placeholder (get-translation "find-and-replace-modal.find.placeholder" "Text to find")
        :on-change #(re-frame/dispatch [::events/set-find-val (-> % .-target .-value)])}]
      [:span.field-helper
       (get-translation "find-and-replace-modal.find.helper"
                        "* Minimum of 2 characters | Case-sensitive")]]
     [:div.g2-field-container
      [field-label (get-translation "find-and-replace-modal.fields.label" "Apply to Fields") true]
      [selector/new-select
       {:items sorted-fields
        :key-fn first
        :label-fn second
        :multi? true
        :value (filter #(contains? selected-fields (first %)) sorted-fields)
        :placeholder (get-translation "view-editor.select-fields" "Select Fields")
        :container (.-body js/document)
        :z-index 1060 ;; above Bootstrap's $zindex-modal (1055) now that this escapes the modal
        :on-change (fn [selected]
                     (re-frame/dispatch [::events/set-selected-fields (set (map first selected))]))}]
      [:span.field-helper
       (get-translation "find-and-replace-modal.fields.helper-prefix" "Supported only Text/Memo fields, ")
       [:b (get-translation "find-and-replace-modal.fields.helper-bold" "Can Not be applied on")]
       " "
       (get-translation "find-and-replace-modal.fields.helper-suffix"
                        "Integrated or Read Only Fields / Steps Parameters {{}}")]]
     (when (seq message)
       [:<>
        [:hr.results-divider]
        [info-note/mux-info-note {:message message :class "mux-info-note-no-margin"}]])
     [:div.g2-field-container
      [field-label (get-translation "find-and-replace-modal.replace.label" "Replace with") false]
      [:input.m-input
       {:type "text"
        :value replace-val
        :placeholder (get-translation "find-and-replace-modal.replace.placeholder" "Replacement text")
        :on-change #(re-frame/dispatch [::events/set-replace-val (-> % .-target .-value)])}]]
     [:span.field-helper.instances-note
      (get-translation "find-and-replace-modal.instances-note"
                       "Find and Replace in Instances is done from the 'Test Sets & Runs' module.")]]))

(defn- footer [close!]
  (let [input-invalid? @(re-frame/subscribe [::subs/input-invalid?])
        stage @(re-frame/subscribe [::subs/stage])
        finding? @(re-frame/subscribe [::subs/finding?])
        submitting? @(re-frame/subscribe [::subs/submitting?])
        replace-val @(re-frame/subscribe [::subs/replace-val])
        find-disabled? (or input-invalid? finding? submitting?)
        ;; empty? (not str/blank?) - a single space is a valid replacement value, only "" isn't.
        replace-disabled? (or (not= stage :found) finding? submitting? (empty? replace-val))]
    [:div.footer
     [:div.footer-actions
      [buttons/button
       {:text (get-translation "generic.button.cancel" "Cancel")
        :on-click close!}]
      [buttons/button
       {:primary? true
        :disabled? find-disabled?
        :text (get-translation "find-and-replace-modal.find.label" "Find")
        :on-click #(re-frame/dispatch [::events/find])}]
      [buttons/button
       {:primary? true
        :disabled? replace-disabled?
        :text (get-translation "find-and-replace-modal.footer.replace-all" "Replace All")
        :on-click #(re-frame/dispatch [::events/replace-all])}]]]))

(defn find-and-replace-modal []
  (let [open? @(re-frame/subscribe [::subs/open?])
        entity @(re-frame/subscribe [::subs/entity])
        selection @(re-frame/subscribe [::subs/selection])
        state-key @(re-frame/subscribe [::subs/state-key])
        cancel! #(re-frame/dispatch [::events/cancel])]
    [application-provider
     [modal/bootstrap-modal
      {:is-open? open?
       :on-request-close #(re-frame/dispatch [::events/close])
       :close-button? true
       :centered? true
       :scrollable? true
       :enforce-focus? false
       :class "find-and-replace-modal batch-modal"}
      [header entity selection state-key]
      [body]
      [footer cancel!]]]))
